import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import Wizard, { getTooltipFromValidationErrors } from '../containers/Modal/Wizard';
import { Input, SelectField } from '../containers/Form';
import Strings from '../Strings';
import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import { actions, ALERT_DEFINITION_DELETE_RESULT, ALERT_DEFINITION_GET_RESULT, ALERT_DEFINITION_PUT_RESULT, GOALS_DELETE_RESULT, GOALS_GET_RESULT, GOALS_PUT_RESULT } from '../pages/SuperUser/Patients/Dashboard/actions';
import { convertCelsiusToFahrenheit, convertFahrenheitToCelsius, convertKilogramToPound, convertPoundToKilogram, roundToDecimal } from '../utils/unitConverters';
import { VITALS } from '../utils/readings';
import { hasPermission, PERMISSIONS } from '../utils/userPermissions';

export const EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME = 'edit-vitals-limits-and-goals-modal';

export const defaultAlertDefinitions = {
  blood_pressure: {
    type: "blood_pressure",
    conditions: [{field: VITALS.PRESSURE_SYSTOLIC, max: null, min: null}, {field: VITALS.PRESSURE_DIASTOLIC, max: null, min: null}],
    priority: 1
  },
  weight: {
    type: "weight",
    conditions: [{field: VITALS.WEIGHT, max: null, min: null}],
    priority: 1
  },
  glucose: {
    type: "glucose",
    conditions: [{field: VITALS.SUGAR_LEVEL, max: null, min: null}],
    priority: 1
  },
  temperature: {
    type: "temperature",
    conditions: [{field: VITALS.TEMPERATURE, max: null, min: null}],
    priority: 1
  },
  heart_rate: {
    type: "heart_rate",
    conditions: [{field: VITALS.HEART_RATE, max: null, min: null}],
    priority: 1
  },
  saturation: {
    type: "saturation",
    conditions: [{field: VITALS.OXYGEN_SATURATION, min: null}],
    priority: 1
  },
  spirometer: {
    type: "spirometer",
    conditions: [{field: VITALS.FORCED_EXPIRATORY_VOLUME_1S, max: null, min: null}, {field: VITALS.FORCED_VITAL_CAPACITY, max: null, min: null}],
    priority: 1
  },
  air_quality: {
    type: "air_quality",
    conditions: [{field: VITALS.PM25, max: null}],
    priority: 1
  },
  coagulation: {
    type: "coagulation",
    conditions: [{field: VITALS.PROTHROMBIN_TIME, max: null, min: null}, {field: VITALS.INTERNATIONAL_NORMALIZED_RATIO, max: null, min: null}],
    priority: 1
  }
}

export const defaultGoals = {
  blood_pressure: {
    type: "blood_pressure",
    goals: [{field: "pressure_systolic", max: null, min: null}, {field: "pressure_diastolic", max: null, min: null}],
  },
  weight: {
    type: "weight",
    goals: [{field: "weight", max: null, min: null}],
  },
  glucose: {
    type: "glucometer",
    goals: [{field: "sugar_level", max: null, min: null}],
  },
  heart_rate: {
    type: "heart_rate",
    goals: [{field: "heart_rate", max: null, min: null}],
  },
  temperature: {
    type: "temperature",
    goals: [{field: "temperature", max: null, min: null}],
  },
  saturation: {
    type: "pulseox",
    goals: [{field: "oxygen_saturation", max: null, min: null}],
  },
}

const vitalToStringMap = {
  'weight': Strings.weight,
  'blood_pressure': Strings.bloodPressure,
  'glucose': Strings.glucose,
  'glucometer': Strings.glucose,
  'temperature': Strings.temperature,
  'spirometer': Strings.spirometer,
  'saturation': Strings.saturation,
  'pulseox': Strings.saturation,
  'heart_rate': Strings.heartRate,
  'air_quality': Strings.airQuality,
  'coagulation': Strings.coagulation,
};

const convertMinMaxUnit = (valueList, vital, direction, doRounding = true, roundTo = 2) => {
  if (vital === 'weight') {
    const converter = direction === 'toImperial' ? convertKilogramToPound : convertPoundToKilogram;
    return valueList?.map(value =>
      value ? {
        ...value,
        min: value.min == null ? null : doRounding ? roundToDecimal(converter(value.min), roundTo) : converter(value.min),
        max: value.max == null ? null : doRounding ? roundToDecimal(converter(value.max), roundTo) : converter(value.max),
      } : value
    ) || [];
  }
  if (vital === 'temperature') {
    const converter = direction === 'toImperial' ? convertCelsiusToFahrenheit : convertFahrenheitToCelsius;
    return valueList?.map(value =>
      value ? {
        ...value,
        min: value.min == null ? null : doRounding ? roundToDecimal(converter(value.min), roundTo) : converter(value.min),
        max: value.max == null ? null : doRounding ? roundToDecimal(converter(value.max), roundTo) : converter(value.max),
      } : value
    ) || [];
  }
  return valueList;
}

const convertDefinitionUnit = (definition, vital, direction, doRounding = true, roundTo = 2) => {
  if (!definition) {
    return definition;
  }
  if (vital === 'weight') {
    return {
      ...definition,
      conditions: convertMinMaxUnit(definition.conditions, vital, direction, doRounding, roundTo),
      goals: convertMinMaxUnit(definition.goals, vital, direction, doRounding, roundTo),
    }
  }
  if (vital === 'temperature') {
    return {
      ...definition,
      conditions: convertMinMaxUnit(definition.conditions, vital, direction, doRounding, roundTo),
      goals: convertMinMaxUnit(definition.goals, vital, direction, doRounding, roundTo),
    }
  }
  return definition;
}

const normalizeGoalsForSingleValue = (vital, definition) => {
  if (vital !== 'blood_pressure' || !definition?.goals) {
    return definition;
  }

  return {
    ...definition,
    goals: definition.goals.map(goal => {
      const value = goal.min ?? goal.max;
      return {
        ...goal,
        min: value,
        max: value,
      };
    }),
  };
}

function EditVitalsLimitsAndGoalsModal(props) {
  const {
    data,
    getAlertDefinition,
    setAlertDefinition,
    deleteAlertDefinition,
    getGoals,
    saveGoals,
    deleteGoal,
    showNotification,
    closeModalWithNextAction,
  } = props;
  const { patientId, vital } = data || {};
  const [alertThresholds, setAlertThresholds] = React.useState(null);
  const [originalAlertThresholds, setOriginalAlertThresholds] = React.useState(null);
  const [goals, setGoals] = React.useState(null);
  const [originalGoals, setOriginalGoals] = React.useState(null);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    getAlertDefinition(patientId, vital).then(resp => {
      if (resp.type === ALERT_DEFINITION_GET_RESULT) {
        setAlertThresholds(
          resp.response?.conditions?.length ? convertDefinitionUnit(resp.response, vital, 'toImperial') : defaultAlertDefinitions[vital]
        );
        setOriginalAlertThresholds(
          resp.response?.conditions?.length ? convertDefinitionUnit(resp.response, vital, 'toImperial') : defaultAlertDefinitions[vital]
        );
      }
    });
    if (defaultGoals[vital]) {
      getGoals(patientId, defaultGoals[vital]?.type).then(resp => {
        if (resp.type === GOALS_GET_RESULT) {
          const goalsFromResp = resp.response?.goals?.length ? convertDefinitionUnit(resp.response, vital, 'toImperial') : null;
          const goalsToSet = !goalsFromResp ? defaultGoals[vital] : {
            type: defaultGoals[vital].type,
            goals: defaultGoals[vital].goals.map(g => {
              const goalFromResp = goalsFromResp.goals.find(gr => gr.field === g.field);
              return goalFromResp ? { ...g, min: goalFromResp.min, max: goalFromResp.max } : g;
            })
          };
          const normalizedGoals = normalizeGoalsForSingleValue(vital, goalsToSet);
          setGoals(normalizedGoals);
          setOriginalGoals(normalizedGoals);
        }
      });
    }
  }, [getAlertDefinition, getGoals, patientId, vital]);

  React.useEffect(() => {
    const errors = [];
    // errors.push({ property: 'name', propertyName: Strings.name, missing: true });
    alertThresholds?.conditions?.forEach((condition, index) => {
      if (condition?.min > condition?.max) {
        errors.push({
          property: `condition_${condition.field}`,
          index, 
          propertyName: fieldToStringMap[`low_${condition.field}`],
          errors: [Strings.formatString(Strings.xMustBeLessThenY, fieldToStringMap[`low_${condition.field}`], fieldToStringMap[`high_${condition.field}`])],
        });
        errors.push({
          property: `condition_${condition.field}`,
          index,
          propertyName: fieldToStringMap[`high_${condition.field}`],
          errors: [Strings.formatString(Strings.xMustBeLessThenY, fieldToStringMap[`low_${condition.field}`], fieldToStringMap[`high_${condition.field}`])],
        });
      }
    });
    goals?.goals?.forEach((goal, index) => {
      if (vital !== 'blood_pressure' && goal?.min !== null && goal?.max !== null && goal?.min > goal?.max) {
        errors.push({
          property: `goal_${goal.field}`,
          index,
          propertyName: fieldToStringMap[`${goal.field}_from`],
          errors: [Strings.formatString(Strings.xMustBeLessThenY, fieldToStringMap[`${goal.field}_from`], fieldToStringMap[`${goal.field}_to`])]
        });
        errors.push({
          property: `goal_${goal.field}`,
          index,
          propertyName: fieldToStringMap[`${goal.field}_to`],
          errors: [Strings.formatString(Strings.xMustBeLessThenY, fieldToStringMap[`${goal.field}_from`], fieldToStringMap[`${goal.field}_to`])]
        });
      }
    });
    setValidationErrors(errors);
  }, [alertThresholds, goals]);

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    if (!isEqual(alertThresholds, originalAlertThresholds)) {
      if (alertThresholds?.conditions?.every(c => (c.min === null || c.min === undefined) && (c.max === null || c.max === undefined))
        && originalAlertThresholds?.conditions?.some(c => (c.min !== null && c.min !== undefined) || (c.max !== null && c.max !== undefined))) {
        const res = await deleteAlertDefinition(patientId, vital);
        if (res.type !== ALERT_DEFINITION_DELETE_RESULT) {
          showNotification(res?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
          setIsSaving(false);
          return;
        }
        showNotification(Strings.formatString(Strings.itemSaved, Strings.alertThreshold));
      } else {
        const alertThresholdsToSave = convertDefinitionUnit(alertThresholds, vital, 'toMetric', false);
        const res = await setAlertDefinition(patientId, alertThresholdsToSave);
        if (res.type !== ALERT_DEFINITION_PUT_RESULT) {
          showNotification(res?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
          setIsSaving(false);
          return;
        }
        showNotification(Strings.formatString(Strings.itemSaved, Strings.alertThreshold));
      }
    }
    if (!isEqual(goals, originalGoals)) {
      if (goals?.goals?.every(g => g.min === null && g.max === null) && originalGoals?.goals?.some(g => g.min !== null || g.max !== null)) {
        const res = await deleteGoal(patientId, vital);
        if (res.type !== GOALS_DELETE_RESULT) {
          showNotification(res?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
          setIsSaving(false);
          return;
        }
        showNotification(Strings.formatString(Strings.itemSaved, Strings.goals));
      } else {
        const goalsToSave = convertDefinitionUnit(goals, vital, 'toMetric', false);
        goalsToSave.goals = goalsToSave.goals.filter(g => g.min !== null || g.max !== null);
        const res = await saveGoals(patientId, vital, goalsToSave);
        if (res.type !== GOALS_PUT_RESULT) {
          showNotification(res?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
          setIsSaving(false);
          return;
        }
        showNotification(Strings.formatString(Strings.itemSaved, Strings.goals));
      }
    }

    closeModalWithNextAction();
  };

  const fieldToStringMap = {
    low_weight: Strings.lowWeight,
    high_weight: Strings.highWeight,
    low_pressure_systolic: Strings.lowSystolic,
    high_pressure_systolic: Strings.highSystolic,
    low_pressure_diastolic: Strings.lowDiastolic,
    high_pressure_diastolic: Strings.highDiastolic,
    low_sugar_level: Strings.lowGlucose,
    high_sugar_level: Strings.highGlucose,
    low_temperature: Strings.lowTemperature,
    high_temperature: Strings.highTemperature,
    low_heart_rate: Strings.lowHR,
    high_heart_rate: Strings.highHR,
    low_saturation: Strings.lowSaturation,
    high_saturation: Strings.highSaturation,
    low_forced_expiratory_volume_1s: Strings.lowFev1,
    high_forced_expiratory_volume_1s: Strings.highFev1,
    low_forced_vital_capacity: Strings.lowFvc,
    high_forced_vital_capacity: Strings.highFvc,
    high_pm25: Strings.highPm25,
    low_prothrombin_time: Strings.lowPt,
    high_prothrombin_time: Strings.highPt,
    low_international_normalized_ratio: Strings.lowInr,
    high_international_normalized_ratio: Strings.highInr,
    weight_from: Strings.weightFrom || 'Weight From',
    weight_to: Strings.weightTo || 'Weight To',
    pressure_systolic_from: Strings.systolicFrom || 'Systolic From',
    pressure_systolic_to: Strings.systolicTo || 'Systolic To',
    pressure_diastolic_from: Strings.diastolicFrom || 'Diastolic From',
    pressure_diastolic_to: Strings.diastolicTo || 'Diastolic To',
    pressure_systolic_single: Strings.systolicPressure || 'Systolic',
    pressure_diastolic_single: Strings.diastolicPressure || 'Diastolic',
    sugar_level_from: Strings.glucoseFrom || 'Glucose From',
    sugar_level_to: Strings.glucoseTo || 'Glucose To',
    temperature_from: Strings.temperatureFrom || 'Temperature From',
    temperature_to: Strings.temperatureTo || 'Temperature To',
    heart_rate_from: Strings.heartRateFrom || 'Heart Rate From',
    heart_rate_to: Strings.heartRateTo || 'Heart Rate To',
    oxygen_saturation_from: Strings.saturationFrom || 'Saturation From',
    oxygen_saturation_to: Strings.saturationTo || 'Saturation To',
  };

  const pages = [
    {
      id: 'set-vitals-limits-and-goals-page',
      title: vitalToStringMap[vital],
      content: (
        <>
          {hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_ALERT_TRESHOLDS) && (
            <>
              <div className="page-info-header">{Strings.alertThresholds}</div>
              {alertThresholds?.conditions?.map((condition, index) => (
                <div className="horizontal-flex" key={condition.field}>
                  {Object.hasOwn(condition, 'min') && (
                    <Input
                      name={`low_${vital}`}
                      id={`low_${vital}`}
                      type="number"
                      label={fieldToStringMap[`low_${condition.field}`]}
                      value={condition.min}
                      onChange={e => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                        setAlertThresholds(prev => ({
                          ...prev,
                          conditions: prev.conditions.map((c, i) => i === index ? { ...c, min: value } : c)
                        }))
                      }}
                      errorsForTooltip={validationErrors.filter(v => v.property === `condition_${condition.field}` && v.index === index)?.[0]?.errors}
                      highlightInvalid={highlightInvalidFields}
                    />
                  )}
                  {Object.hasOwn(condition, 'max') && (
                    <Input
                      name={`high_${vital}`}
                      id={`high_${vital}`}
                      type="number"
                      label={fieldToStringMap[`high_${condition.field}`]}
                      value={condition.max}
                      onChange={e => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                        setAlertThresholds(prev => ({
                          ...prev,
                          conditions: prev.conditions.map((c, i) => i === index ? { ...c, max: value } : c)
                        }))
                      }}
                      errorsForTooltip={validationErrors.filter(v => v.property === `condition_${condition.field}` && v.index === index)?.[0]?.errors}
                      highlightInvalid={highlightInvalidFields}
                    />
                  )}
                </div>
              ))}
              <div className="control-with-top-right-text double-width-left">
                <SelectField
                  name="priority"
                  id="priority"
                  label={Strings.capPatient.sendAlertNotificationTo}
                  data={[{ value: 1, label: Strings.caregivers }, { value: 2, label: Strings.capPatient.caregiversAndCareprovider }]}
                  value={alertThresholds?.priority}
                  onChange={option => setAlertThresholds(prev => ({
                    ...prev,
                    priority: option.value,
                  }))}
                  disabled={!alertThresholds?.conditions?.some(c => c.min !== null || c.max !== null)}
                />
                <div className="right-top-text">
                  <span
                    data-tooltip-id="tooltip"
                    data-tooltip-html={Strings.capPatient.alertRecipientsTooltip}
                    className="icon"
                  >
                    ?
                  </span>
                </div>
              </div>
            </>
          )}
          {hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_VITAL_GOALS) && (
            <>
              {goals && <div className="page-info-header">{Strings.goals}</div>}
              {vital === 'blood_pressure' ? (
                <div className="horizontal-flex">
                  {goals?.goals?.map((goal, index) => (
                    <Input
                      name={`${goal.field}_goal`}
                      id={`${goal.field}_goal`}
                      type="number"
                      label={fieldToStringMap[`${goal.field}_single`]}
                      value={goal.min ?? goal.max}
                      key={goal.field}
                      onChange={e => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                        setGoals(prev => ({
                          ...prev,
                          goals: prev.goals.map((c, i) => i === index ? { ...c, min: value, max: value } : c)
                        }))
                      }}
                      errorsForTooltip={validationErrors.filter(v => v.property === `goal_${goal.field}` && v.index === index)?.[0]?.errors}
                      highlightInvalid={highlightInvalidFields}
                    />
                  ))}
                </div>
              ) : (
                goals?.goals?.map((goal, index) => (
                  <div className="horizontal-flex" key={goal.field}>
                    <>
                      {Object.hasOwn(goal, 'min') && (
                        <Input
                          name={`low_${vital}`}
                          id={`low_${vital}`}
                          type="number"
                          label={fieldToStringMap[`${goal.field}_from`]}
                          value={goal.min}
                          onChange={e => {
                            const value = e.target.value === '' ? null : parseFloat(e.target.value);
                            setGoals(prev => ({
                              ...prev,
                              goals: prev.goals.map((c, i) => i === index ? { ...c, min: value } : c)
                            }))
                          }}
                          errorsForTooltip={validationErrors.filter(v => v.property === `goal_${goal.field}` && v.index === index)?.[0]?.errors}
                          highlightInvalid={highlightInvalidFields}
                        />
                      )}
                      {Object.hasOwn(goal, 'max') && (
                        <Input
                          name={`high_${vital}`}
                          id={`high_${vital}`}
                          type="number"
                          label={fieldToStringMap[`${goal.field}_to`]}
                          value={goal.max}
                          onChange={e => {
                            const value = e.target.value === '' ? null : parseFloat(e.target.value);
                            setGoals(prev => ({
                              ...prev,
                              goals: prev.goals.map((c, i) => i === index ? { ...c, max: value } : c)
                            }))
                          }}
                          errorsForTooltip={validationErrors.filter(v => v.property === `goal_${goal.field}` && v.index === index)?.[0]?.errors}
                          highlightInvalid={highlightInvalidFields}
                        />
                      )}
                    </>
                  </div>
                ))
              )}
            </>
          )}
        </>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !isSaving,
      nextButton: { text: Strings.save },
    },
  ];

  return (
    <Wizard
      name={EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      showPagesFilter={false}
      nextButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
    />
  );
}

EditVitalsLimitsAndGoalsModal.propTypes = {
  data: PropTypes.shape({
    template: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
      owner: PropTypes.string,
      owner_uuid: PropTypes.string,
    }),
  }),
  onCancel: PropTypes.func,
  getOrganizations: PropTypes.func,
  updateOrgTemplateEdit: PropTypes.func,
  getOrgTemplates: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getAlertDefinition: (patientId, vital) => dispatch(actions.getAlertDefinition(patientId, vital)),
  getGoals: (patientId, vital) => dispatch(actions.getGoals(patientId, vital)),
  setAlertDefinition: (patientId, alertDefinition) => dispatch(actions.setAlertDefinition(patientId, alertDefinition)),
  deleteAlertDefinition: (patientId, vital) => dispatch(actions.deleteAlertDefinition(patientId, vital)),
  saveGoals: (patientId, vital, goals) => dispatch(actions.setGoals(patientId, vital, goals)),
  deleteGoal: (patientId, vital) => dispatch(actions.deleteGoal(patientId, vital)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
  closeModalWithNextAction: () => {
    dispatch(closeModal(EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(EditVitalsLimitsAndGoalsModal);
